import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as Downarrow } from '../../../assets/icons/down-arrow.svg';
import { ReactComponent as LocationIcon } from '../../../assets/icons/location-icon.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as StationIcon } from '../../../assets/icons/sidebar/sidebar-account.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  AvailableMaintenance,
  // FilterScheduleInterFace,
  RequestScheduleInterFace,
} from '../../../interface/maintenance-scheduling.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  disableMaintenanceManagementFiltersApplied,
  fetchAllMaintenanceSchedules,
  fetchMaintenanceManagementListFilters,
  setScheduleFilterData,
  updateMaintenanceManagementFilters,
  setSortData,
} from '../../../redux/slice/MaintenanceScheduleManagementSlice';
import { setNotificationDetails } from '../../../redux/slice/NotificationSlice';
import { setPageTitle } from '../../../redux/slice/PageTitleSlice';
import { setSelectedStation } from '../../../redux/slice/StationManagementSidePanelSlice';
import { RootState } from '../../../redux/store';
import MyButton from '../../../shared-components/button/Button';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import DropDownArrow from '../../../shared-components/drop-down-menu/DropDownMenu';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import DeviceViewSidePanel from '../../../shared-components/table-side-panel/DeviceViewSidePanel';
import StationViewSidePanel from '../../../shared-components/table-side-panel/StationViewSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { removeEmptyKeys } from '../../../shared-functions/DataTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../shared-functions/FormatUtcString';
import maintenanceScheduleApi from '../../../utils/api/maintenance-schedule';

import MaintenanceScheduling from './MaintenanceScheduling';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
}

/**
 * Management management page
 */
function MaintenanceManagement() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('maintenanceSchedule');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [urlFiltersApplied, setUrlFiltersApplied] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [sidePanelOpen, setSidePanelOpen] = useState<boolean>(false);
  const [searchParameters] = useSearchParams();
  const [modalBoxDetail, setModalBoxDetails] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
  });
  const {
    schedules,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filter,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((states: RootState) => states.maintenanceSchedule);

  /**
   * Fetch the date for using redux function
   * @param data
   */
  const fetchData = (data: RequestScheduleInterFace) => {
    if (!loading) {
      dispatch(fetchAllMaintenanceSchedules(data));
    }
  };
  /**
   * Handle status change
   * @param rowId
   * @param newStatus
   */
  const handleStatusChange = (rowId: string | number | null, newStatus: string) => {
    maintenanceScheduleApi
      .editMaintenanceSchedule(rowId, { status: newStatus })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetails({
          title: '',
          header: false,
          content: (
            <SuccessNotification
              title={t('statusUpdatedSuccessfully')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                fetchData({
                  page,
                  limit,
                });
                setModalBoxStatus('close');
              }}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };
  /**
   * handlePageChange
   */
  const handlePageChange = (pageCount: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: pageCount,
      limit,
    });
  };
  /**
   * handleLimitChange
   */
  const handleLimitChange = (limitCount: number) => {
    fetchData({
      ...filter,
      page: DefaultApiLimit.page,
      limit: limitCount,
    });
  };
  // /**
  //  * Handle the filter submit function
  //  */
  // const handleSubmitFilter = (filterData: FilterScheduleInterFace) => {
  //   dispatch(setScheduleFilterData(filterData));
  //   fetchData({
  //     page: DefaultApiLimit.page,
  //     limit,
  //     ...filterData,
  //   });
  //   setModalBoxStatus('close');
  // };

  // /**
  //  * Handle the filter data clear function
  //  */
  // const filterClearFunction = () => {
  //   if (Object.keys(filter).length > 0) {
  //     dispatch(setScheduleFilterData({}));
  //     fetchData({
  //       page: DefaultApiLimit.page,
  //       limit,
  //     });
  //   }
  // };

  /**
   *  Handle the filter data clear function
   * @param filterData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setScheduleFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField === 'time' ? 'createdAt' : sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to open device view model
   */
  const handleOpenStationManagement = (
    chargingSessionDate: AvailableMaintenance,
    newTab: boolean,
  ) => {
    if (chargingSessionDate) {
      dispatch(
        setSelectedStation({
          id: chargingSessionDate?.chargingStation,
          name: chargingSessionDate?.chargingStationName,
        }),
      );
      if (newTab) {
        const url = `/ev-admin/maintenance-scheduling/station/${chargingSessionDate.chargingStation}/station-dashboard?searchBy=${chargingSessionDate.chargingStationName}`;
        window.open(url, '_blank'); // opens in a new tab
      } else {
        navigate({
          pathname: `/ev-admin/maintenance-scheduling/station/${chargingSessionDate.chargingStation}/station-dashboard`,
          search: `?searchBy=${chargingSessionDate.chargingStationName}`,
        });
      }
    } else {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 200,
            message: t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  // /**
  //  * filter function
  //  */
  // const FilterModal = () => {
  //   setModalBoxStatus('open');
  //   setModalBoxDetails({
  //     title: '',
  //     header: false,
  //     notificationModal: false,
  //     content: (
  //       <MaintenanceFilter
  //         handleCancel={() => setModalBoxStatus('close')}
  //         handleSubmitFilter={handleSubmitFilter}
  //       />
  //     ),
  //   });
  // };

  /**
   * createDropdownList
   */
  const createDropdownList = (maintenanceId: number | string | null) => [
    {
      value: '1', // Changed from 'id' to 'value'
      content: <p style={{ color: 'orange' }}>{t('inProgress')}</p>,
      key: 'inProgress',
      /**
       * createDropdownList
       */
      onClickFunc: () => handleStatusChange(maintenanceId, 'inProgress'),
    },
    {
      value: '2', // Changed from 'id' to 'value'
      content: <p style={{ color: 'green' }}>{t('completed')}</p>,
      key: 'completed',
      /**
       * createDropdownList
       */
      onClickFunc: () => handleStatusChange(maintenanceId, 'completed'),
    },
    {
      value: '3', // Changed from 'id' to 'value'
      content: <p style={{ color: 'red' }}>{t('postponed')}</p>,
      key: 'postponed',
      /**
       * createDropdownList
       */
      onClickFunc: () => handleStatusChange(maintenanceId, 'postponed'),
    },
  ];

  const column = [
    {
      label: t('maintenanceDate'),
      accessorKey: 'createdAt' as keyof AvailableMaintenance,
      sortAllow: true,
      /**
       * customData
       */
      customData: (maintenance: AvailableMaintenance) => (
        <div className="table-row-each-data">{maintenance.date}</div>
      ),
    },
    {
      label: t('maintenanceTime'),
      accessorKey: 'time' as keyof AvailableMaintenance,
      sortAllow: true,
      /**
       * customData
       */
      customData: (maintenance: AvailableMaintenance) => (
        <div className="table-row-each-data">{maintenance.time}</div>
      ),
    },
    {
      label: t('stationName'),
      accessorKey: 'name' as keyof AvailableMaintenance,
      sortAllow: false,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationViewSidePanel fetchIdFrom="queryParams" />,
      handleClickExpandView: handleOpenStationManagement,
    },
    {
      label: t('stationLocation'),
      accessorKey: 'stationLocation' as keyof AvailableMaintenance,
      sortAllow: false,
      /**
       * maintenance
       */
      customData: (maintenance: AvailableMaintenance) => (
        <div className="location-in-maintenance-table">
          <span>
            <LocationIcon />
          </span>
          <p className="table-row-each-data">{maintenance.address}</p>
        </div>
      ),
    },
    {
      label: t('deviceName'),
      accessorKey: 'chargingDeviceName' as keyof AvailableMaintenance,
      sortAllow: false,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPoint',
      sidePanelComponent: <DeviceViewSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('task'),
      accessorKey: 'type' as keyof AvailableMaintenance,
      sortAllow: false,
    },
    {
      label: t('duration'),
      accessorKey: 'duration' as keyof AvailableMaintenance,
      sortAllow: true,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof AvailableMaintenance,
      /**
       * maintenance
       */
      customData: (maintenance: AvailableMaintenance) => (
        <div className="status-wrapper-dropdown">
          {createDropdownList(maintenance.id).find((value) => value.key === maintenance.status)
            ?.content || t(maintenance.status as any)}
          {maintenance.status !== 'completed' && maintenance.status !== 'postponed' && (
            <DropDownArrow
              rowId={maintenance.id?.toString() || ''} // Convert id to string
              dropDownIcon={<Downarrow />}
              dropDownList={createDropdownList(maintenance.id)}
              lineBetweenElement
            />
          )}
        </div>
      ),
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('maintenanceDate'), accessorKey: 'createdAt' },
    { label: t('maintenanceTime'), accessorKey: 'time' },
    { label: t('stationName'), accessorKey: 'name' },
    { label: t('stationLocation'), accessorKey: 'stationLocation' },
    { label: t('deviceName'), accessorKey: 'chargingDeviceName' },
    { label: t('task'), accessorKey: 'type' },
    { label: t('duration'), accessorKey: 'duration' },
    { label: t('status'), accessorKey: 'status' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);
    const queryParameters = GetParametersFromCommonFilters(filters);
    maintenanceScheduleApi
      .fetchMaintenanceScheduleExport(removeEmptyKeys({ ...queryParameters, sortField, sortBy }))
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.results.map((row: AvailableMaintenance, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'createdAt':
                return ConvertUTCtoDate(row.createdAt);
              case 'time':
                return FormatUtcString(ConvertUTCtoHHMM(row.createdAt));
              case 'name':
                return row.chargingStationName;
              case 'stationLocation':
                return row.chargingStationAddress;
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${'maintenanceScheduleList'}.csv`);
          } else {
            exportPDF(headers, rows, `${'maintenanceScheduleList'}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Add maintenance schedule function
   */
  const addMaintenanceSchedule = () => {
    setSidePanelOpen(true);
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (!urlFiltersApplied) return;

    if (isFilterUpdated || schedules.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      fetchData({
        ...queryParameters,
        page: DefaultApiLimit.page,
        limit: DefaultApiLimit.limit,
      });

      dispatch(disableMaintenanceManagementFiltersApplied());
    }
  }, [filters, urlFiltersApplied]);

  useEffect(() => {
    const hasSidePanelParameter = searchParameters.get('stationId');

    if (!hasSidePanelParameter) {
      dispatch(
        setPageTitle({
          title: t('maintenanceSchedule'),
          icon: <StationIcon />,
          count: totalResults,
        }),
      );
    } else {
      dispatch(
        setPageTitle({
          title: t('maintenanceSchedule'),
          icon: <StationIcon />,
          count: undefined,
        }),
      );
    }
  }, [totalResults, searchParameters, t]);

  useEffect(() => {
    const stationName = searchParameters.get('stationName');
    const status = searchParameters.get('status');
    const startDate = searchParameters.get('startDate');
    const endDate = searchParameters.get('endDate');

    if (!status && !startDate && !endDate && !stationName) {
      setUrlFiltersApplied(true);
      return;
    }

    /**
     * Function to apply filters from URL parameters
     */
    const applyFilters = () => {
      // Clear filters once if any URL parameter exists
      dispatch(
        updateMaintenanceManagementFilters({
          key: '',
          value: '',
          type: 'clear',
        }),
      );

      if (status) {
        dispatch(
          updateMaintenanceManagementFilters({
            key: 'status',
            value: status,
            type: 'add',
          }),
        );
      }

      if (startDate) {
        dispatch(
          updateMaintenanceManagementFilters({
            key: 'dateRange',
            subKey: 'startDate',
            value: startDate,
            type: 'add',
          }),
        );
      }

      if (endDate) {
        dispatch(
          updateMaintenanceManagementFilters({
            key: 'dateRange',
            subKey: 'endDate',
            value: endDate,
            type: 'add',
          }),
        );
      }

      if (stationName) {
        dispatch(
          updateMaintenanceManagementFilters({
            key: 'stationName',
            value: stationName,
            type: 'add',
          }),
        );
      }

      setUrlFiltersApplied(true);
    };

    if (filters.length === 1) {
      // filters list is not yet loaded (only searchBy exists)
      dispatch(fetchMaintenanceManagementListFilters()).then(() => {
        applyFilters();
      });
    } else {
      applyFilters();
    }
  }, [searchParameters, filters.length, dispatch]);

  return (
    <div className="maintenance-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="table-container">
        <CustomTable
          dataKey="MaintenanceManagement"
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={schedules}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateMaintenanceManagementFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchMaintenanceManagementListFilters}
          // filterFunction={FilterModal}
          // filterClearFunction={filterClearFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refreshRequired
          refreshCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          serialNo
          flexDirection
          onHiddenColumnsChange={setHiddenColumns}
          leftTopContent={
            <MyButton
              label={t('addMaintenanceSchedule')}
              buttonType="submit"
              onClickFunc={addMaintenanceSchedule}
              svgIcon={<PlusIcon />}
            />
          }
          sidePanelEnabled={sidePanelOpen}
          sidePanelManualContent={
            <MaintenanceScheduling
              onClose={() => setSidePanelOpen(false)}
              onSuccess={handleRefresh}
            />
          }
          onSidePanelClose={() => setSidePanelOpen(false)}
        />
      </div>
    </div>
  );
}

export default MaintenanceManagement;
